import React, { useMemo } from "react";
import { Clock3, Loader2, TrendingUp, Users } from "lucide-react";
import {
  compactNumber,
  formatPeakHour,
  formatViewerCount,
} from "../../utils/viewersDeskHelpers";
import AudienceConversionCard from "./AudienceConversionCard";
import AudienceStickinessCard from "./AudienceStickinessCard";
import {
  AudienceMixDonut,
  DailyTrendAreaChart,
  HourlyHeatStrip,
  TopViewersRanked,
} from "./AudienceChartViz";

const AudienceAnalyticsPanel = ({
  pulse,
  loading = false,
  dashboard = null,
  layout = "workspace",
}) => {
  const isWorkspace = layout === "workspace";

  const dailyItems = useMemo(() => {
    if (!pulse?.dailyTrend) return [];
    return pulse.dailyTrend.map((day) => ({
      key: day.date,
      value: day.count,
      label: day.label,
    }));
  }, [pulse?.dailyTrend]);

  if (loading) {
    return (
      <section
        className={`bd-viewer-intel bd-viewer-intel-${layout}`}
        aria-label="Audience analytics"
      >
        <div className="bd-viewer-intel-loading">
          <Loader2 size={14} className="animate-spin" />
          <span>Reading views…</span>
        </div>
      </section>
    );
  }

  if (!pulse?.loaded) {
    return null;
  }

  const totalViews = Number(pulse.totalViews) || 0;
  const unique = Number(pulse.uniqueViewers) || 0;
  const repeat = Number(pulse.repeatViewers) || 0;
  const firstTime = Math.max(0, unique - repeat);

  const dailyHasViews = dailyItems.some((item) => item.value > 0);
  const windowTotal = dailyItems.reduce((sum, item) => sum + item.value, 0);

  const topViewers = (pulse.topViewers || []).filter((row) => Number(row.count) > 0);

  const hourlyItems = Array.isArray(pulse.byHour)
    ? pulse.byHour.map((value, hour) => ({ key: `h-${hour}`, value }))
    : [];
  const hourlyHasViews = hourlyItems.some((item) => item.value > 0);

  return (
    <section
      className={`bd-viewer-intel bd-viewer-intel-${layout}`}
      aria-label="Audience analytics"
    >
      {!isWorkspace ? (
        <header className="bd-audience-analytics-head">
          <TrendingUp size={14} />
          <div>
            <h2>Audience insights</h2>
            <p>
              {totalViews > 0
                ? `From ${formatViewerCount(totalViews)} stored in your house profile.`
                : "Charts appear once profile views are recorded."}
            </p>
          </div>
        </header>
      ) : null}

      {totalViews === 0 ? (
        <p className="bd-audience-empty bd-viewer-intel-empty">
          No profile views recorded yet.
        </p>
      ) : (
        <>
          {isWorkspace ? (
            <div className="bd-viewer-intel-hero">
              <span className="bd-book-kicker">Profile views</span>
              <strong className="tabular-nums">{totalViews.toLocaleString()}</strong>
              <span className="bd-viewer-intel-hero-meta tabular-nums">
                {compactNumber(unique)} unique · {compactNumber(repeat)} repeat
              </span>
            </div>
          ) : null}

          <div className="bd-viewer-intel-grid">
            <AudienceStickinessCard pulse={pulse} compact={isWorkspace} />
            <AudienceConversionCard dashboard={dashboard} compact={isWorkspace} />

            {pulse.dailyTrend ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <TrendingUp size={11} aria-hidden />
                  <div>
                    <span className="bd-book-kicker">Volume · 7d</span>
                    {isWorkspace && dailyHasViews ? (
                      <em className="bd-book-caption tabular-nums">
                        {windowTotal.toLocaleString()} views
                      </em>
                    ) : null}
                  </div>
                </div>
                {dailyHasViews ? (
                  <DailyTrendAreaChart
                    items={dailyItems}
                    ariaLabel="Recorded views over the last seven days"
                  />
                ) : (
                  <p className="bd-audience-empty bd-book-empty">No views in 7d</p>
                )}
              </article>
            ) : null}

            {hourlyHasViews ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <Clock3 size={11} aria-hidden />
                  <div>
                    <span className="bd-book-kicker">Hourly rhythm</span>
                    {pulse.peakHour != null ? (
                      <em className="bd-book-caption">peaks {formatPeakHour(pulse.peakHour)}</em>
                    ) : null}
                  </div>
                </div>
                <HourlyHeatStrip
                  items={hourlyItems}
                  peakIndex={pulse.peakHour ?? null}
                  ariaLabel="Recorded views by hour of day"
                />
              </article>
            ) : null}

            {unique > 0 ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <Users size={11} aria-hidden />
                  <span className="bd-book-kicker">Audience mix</span>
                </div>
                <AudienceMixDonut firstTime={firstTime} repeat={repeat} unique={unique} compact={isWorkspace} />
              </article>
            ) : null}

            {topViewers.length ? (
              <article className="aud-card aud-card-compact">
                <div className="bd-book-card-head">
                  <Users size={11} aria-hidden />
                  <span className="bd-book-kicker">Top viewers</span>
                </div>
                <TopViewersRanked rows={topViewers} />
              </article>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
};

export default AudienceAnalyticsPanel;
